<template>
  <div v-if="multiple" class="grid-area-editor is-multi-selector">
    <common-multi-selector
      v-if="type === optionValue.province || parentValue"
      :data-source="commonProvinceSourceProxy"
      v-model="province.multi"
    />
    <area-cascade-multi-selector
      v-else-if="isCity || isCounty"
      :provinces="provinceSource"
      :deep="deepProxy"
      :show-prefix="showPrefix"
      :hidden-values="hiddenItems"
      :disabled-values="disabledItems"
      :allowed-values="allowedItems"
      v-model="modelProxy"
    />
  </div>
  <div v-else-if="modeValue.single" class="grid-area-editor is-single-selector">
    <common-single-selector
      v-if="type === optionValue.province || parentValue"
      :data-source="commonProvinceSourceProxy"
      v-model="province.single"
    />
    <area-cascade-selector
      v-else-if="isCity || isCounty"
      :provinces="provinceSource"
      :deep="deepProxy"
      :show-prefix="showPrefix"
      :hidden-values="hiddenItems"
      :disabled-values="disabledItems"
      :allowed-values="allowedItems"
      v-model="modelProxy"
    />
  </div>
  <div v-else>不支持的地区输入类型</div>
</template>

<script>
import { Switch } from "element-ui";

import {
  CityOption,
  CountyOption,
  ProvinceOption,
} from "@/components/excel/schema/value/type/area";
import CommonMultiSelector from "@/components/selector/CommonMultiSelector";
import AreaCascadeMultiSelector from "@/components/selector/area/CascadeMultiSelector";
import CommonSingleSelector from "@/components/selector/CommonSingleSelector";
import { AreaSharedCache } from "@/utils/AreaSharedCache";
import AreaCascadeSelector from "@/components/selector/area/CascadeSelector";
import {
  MultiSelector,
  SingleSelector,
} from "@/components/excel/schema/value/mode";
import {
  assembleCommonDisabled,
  filterCommonAllowed,
  filterCommonHidden,
} from "@/utils/excel/OptionFilter";
function finLastItem(array) {
  if (!Array.isArray(array)) {
    return array;
  }
  if (!array.length) {
    return null;
  }
  const item = array[array.length - 1];
  return finLastItem(item);
}
export default {
  name: "grid-area-editor",
  components: {
    AreaCascadeSelector,
    CommonSingleSelector,
    AreaCascadeMultiSelector,
    CommonMultiSelector,
    [Switch.name]: Switch,
  },
  props: {
    type: {
      type: Number,
    },
    text: {
      type: String,
    },
    value: {
      type: [Array, Number],
    },
    parentValue: {
      type: [Array, Number],
    },
    mode: {
      type: Number,
    },
    allowedItems: Array,
    disabledItems: Array,
    hiddenItems: Array,
  },
  computed: {
    commonProvinceSourceProxy() {
      const { disabledValues, hiddenValues, allowedValues } = this;
      const list = this.provinceSource.map(({ id, name }) => ({
        value: id,
        text: name,
      }));
      const allowedList = filterCommonAllowed(list, allowedValues);
      const filter = filterCommonHidden(allowedList, hiddenValues);
      return assembleCommonDisabled(filter, disabledValues);
    },
    showCommonSelector() {
      if (!this.singleRank) {
        return false;
      }
      if (this.parentValue) {
        return true;
      }
      return this.isProvince;
    },
    singleRank() {
      return this.isProvince || this.isCity || this.isCounty;
    },
    multiple() {
      return this.mode === this.modeValue.multi;
    },
    deepProxy() {
      if (this.parentValue) {
        return 1;
      }
      if (this.isProvince) {
        return 1;
      }
      if (this.isCity) {
        return 2;
      }
      if (this.isCounty) {
        return 3;
      }
      return 0;
    },
    modelProxy: {
      get() {
        if (this.showCommonSelector) {
          return this.multiple ? this.province.multi : this.province.single;
        }
        if (this.isCity) {
          return this.multiple ? this.city.multi : this.city.single;
        }
        if (this.isCounty) {
          return this.multiple ? this.county.multi : this.county.single;
        }
        throw new Error("暂未适配的类型");
      },
      set(value) {
        if (this.showCommonSelector) {
          if (this.multiple) {
            this.province.multi = value;
          } else {
            this.province.single = value;
          }
          return;
        }
        if (this.isCity) {
          if (this.multiple) {
            this.city.multi = value;
          } else {
            this.city.single = value;
          }
          return;
        }
        if (this.isCounty) {
          if (this.multiple) {
            this.county.multi = value;
          } else {
            this.county.single = value;
          }
          return;
        }
        throw new Error("暂未适配的类型");
      },
    },
    allowedValues() {
      if (this.existAllowedItems) {
        if (this.isProvince) {
          const { allowedItems } = this.$props;
          return allowedItems;
        }
        if (this.isCity) {
          return this.allowedCities;
        }
        if (this.isCounty) {
          return this.allowedCounties;
        }
      }
      return [];
    },
    hiddenValues() {
      if (this.existHiddenItems) {
        if (this.isProvince) {
          const { hiddenItems } = this.$props;
          return hiddenItems;
        }
        if (this.isCity) {
          return this.hiddenCities;
        }
        if (this.isCounty) {
          return this.hiddenCounties;
        }
      }
      return [];
    },
    disabledValues() {
      if (this.existDisabledItems) {
        if (this.isProvince) {
          const { disabledItems } = this.$props;
          return disabledItems;
        }
        if (this.isCity) {
          return this.disabledCities;
        }
        if (this.isCounty) {
          return this.disabledCounties;
        }
      }
      return [];
    },
    existAllowedItems() {
      const { allowedItems } = this.$props;
      return Array.isArray(allowedItems) && allowedItems.length > 0;
    },
    existHiddenItems() {
      const { hiddenItems } = this.$props;
      return Array.isArray(hiddenItems) && hiddenItems.length > 0;
    },
    existDisabledItems() {
      const { disabledItems } = this.$props;
      return Array.isArray(disabledItems) && disabledItems.length > 0;
    },
    allowedProvinces() {
      if (!this.existAllowedItems) {
        return [];
      }
      const { allowedItems } = this.$props;
      if (this.isProvince) {
        return allowedItems;
      }
      if (this.isCity || this.isCounty) {
        return allowedItems.map((it) => it[0]);
      }
      throw new Error("暂未适配的类型");
    },
    allowedCities() {
      if (!this.existAllowedItems) {
        return [];
      }
      const { allowedItems } = this.$props;
      if (this.isProvince) {
        return [];
      }
      if (this.isCity) {
        return allowedItems.flatMap((it) => it[1]);
      }
      if (this.isCounty) {
        return allowedItems.map((it) => it[1]);
      }
      throw new Error("暂未适配的类型");
    },
    allowedCounties() {
      if (!this.existAllowedItems) {
        return [];
      }
      const { allowedItems } = this.$props;
      if (this.isProvince || this.isCity) {
        return [];
      }
      if (this.isCounty) {
        return allowedItems.flatMap((it) => it[2]);
      }
      throw new Error("暂未适配的类型");
    },
    hiddenProvinces() {
      if (!this.existHiddenItems) {
        return [];
      }
      const { hiddenItems } = this.$props;
      if (this.isProvince) {
        return hiddenItems;
      }
      if (this.isCity || this.isCounty) {
        return hiddenItems.map((it) => it[0]);
      }
      throw new Error("暂未适配的类型");
    },
    hiddenCities() {
      if (!this.existHiddenItems) {
        return [];
      }
      const { hiddenItems } = this.$props;
      if (this.isProvince) {
        return [];
      }
      if (this.isCity) {
        return hiddenItems.flatMap((it) => it[1]);
      }
      if (this.isCounty) {
        return hiddenItems.map((it) => it[1]);
      }
      throw new Error("暂未适配的类型");
    },
    hiddenCounties() {
      if (!this.existHiddenItems) {
        return [];
      }
      const { hiddenItems } = this.$props;
      if (this.isProvince || this.isCity) {
        return [];
      }
      if (this.isCounty) {
        return hiddenItems.flatMap((it) => it[2]);
      }
      throw new Error("暂未适配的类型");
    },
    disabledProvinces() {
      if (!this.existDisabledItems) {
        return [];
      }
      const { disabledItems } = this.$props;
      if (this.isProvince) {
        return disabledItems;
      }
      if (this.isCity || this.isCounty) {
        return disabledItems.map((it) => it[0]);
      }
      throw new Error("暂未适配的类型");
    },
    disabledCities() {
      if (!this.existDisabledItems) {
        return [];
      }
      const { disabledItems } = this.$props;
      if (this.isProvince) {
        return [];
      }
      if (this.isCity) {
        return disabledItems.flatMap((it) => it[1]);
      }
      if (this.isCounty) {
        return disabledItems.map((it) => it[1]);
      }
      throw new Error("暂未适配的类型");
    },
    disabledCounties() {
      if (!this.existDisabledItems) {
        return [];
      }
      const { disabledItems } = this.$props;
      if (this.isProvince || this.isCity) {
        return [];
      }
      if (this.isCounty) {
        return disabledItems.flatMap((it) => it[2]);
      }
      throw new Error("暂未适配的类型");
    },
    isProvince() {
      return this.type === this.optionValue.province;
    },
    isCity() {
      return this.type === this.optionValue.city;
    },
    isCounty() {
      return this.type === this.optionValue.county;
    },
  },
  watch: {
    province: {
      deep: true,
      handler: "updateTags",
    },
    city: {
      deep: true,
      handler: "updateTags",
    },
    county: {
      deep: true,
      handler: "updateTags",
    },
    showPrefix: "updateTags",
    value(value) {
      this.updateLocalValue(value);
    },
    parentValue(parentValue) {
      this.updateLocalSource(parentValue);
    },
  },
  async mounted() {
    const { value, parentValue } = this.$props;
    this.updateLocalSource(parentValue);
    if (value) {
      this.updateLocalValue(value);
    }
  },
  data() {
    return {
      provinceSource: [],
      separator: "/",
      province: {
        multi: [],
        single: null,
      },
      city: {
        multi: [],
        single: [],
      },
      county: {
        multi: [],
        single: [],
      },
      showPrefix: false,
      optionValue: {
        province: ProvinceOption.value,
        city: CityOption.value,
        county: CountyOption.value,
      },
      modeValue: {
        single: SingleSelector.value,
        multi: MultiSelector.value,
      },
    };
  },
  methods: {
    async updateLocalSource(parentValue) {
      if (this.isProvince) {
        if (this.existAllowedItems) {
          await AreaSharedCache.loadByList(this.allowedProvinces);
          this.provinceSource = await Promise.all(
            this.allowedProvinces.map(
              async (it) => await AreaSharedCache.getItem(it)
            )
          );
        } else {
          this.provinceSource = await AreaSharedCache.loadChildren(0);
        }
        return;
      }
      const validParentValue = this.filterParentValueByAllowedItems(
        parentValue
      );
      const list = await this.loadSource(validParentValue);
      console.log(list);
      this.provinceSource = list || [];
    },
    filterParentValueByAllowedItems(parentValue) {
      // 通过 allowedItems 过滤出允许的 parent value;
      const { allowedProvinces, allowedCities, existAllowedItems } = this;
      if (!existAllowedItems) {
        return parentValue;
      }
      if (Array.isArray(parentValue)) {
        if (this.isCity) {
          return parentValue.filter((it) => allowedProvinces.includes(it));
        }
        if (this.isCounty) {
          return parentValue.filter((it) => allowedCities.includes(it));
        }
        throw new Error("暂未适配的类型");
      }
      if (parentValue) {
        if (this.isCity) {
          if (allowedProvinces.includes(parentValue)) {
            return parentValue;
          }
          return -1;
        }
        if (this.isCounty) {
          if (allowedCities.includes(parentValue)) {
            return parentValue;
          }
          return -1;
        }
        throw new Error("暂未适配的类型");
      }
      if (this.isCity) {
        return allowedProvinces;
      }
      if (this.isCounty) {
        return allowedCities;
      }
      throw new Error("暂未适配的类型");
    },
    async loadSource(parentValue) {
      if (Array.isArray(parentValue)) {
        await AreaSharedCache.loadByList(parentValue);
        await AreaSharedCache.loadChildrenByList(parentValue);
        return parentValue.flatMap((it) => AreaSharedCache.getChildren(it));
      }
      if (parentValue === undefined || parentValue === null) {
        return await AreaSharedCache.loadChildren(0);
      }
      return await AreaSharedCache.loadChildren(parentValue);
    },
    updateLocalValue(value) {
      if (this.multiple) {
        this.province.multi = value;
        this.city.multi = value;
        this.county.multi = value;
      } else {
        this.province.single = value;
        this.city.single = value;
        this.county.single = value;
      }
    },
    updateTags() {
      const value = this.getValue();
      this.emitChangeValue(value);
      const tags = this.getTags();
      this.emitChangeTitle(tags);
      this.emitChange({ value, text: tags.join() });
    },
    getItemName(id) {
      const item = AreaSharedCache.getItem(id);
      return (item && item.name) || id;
    },
    getItemTags(item, showPrefix) {
      if (Array.isArray(item)) {
        // 市和县
        if (!showPrefix) {
          if (this.multiple) {
            const list = item.find((it) => Array.isArray(it));
            if (list && list.length) {
              return list.map((it) => this.getItemName(it));
            }
          } else {
            const last = this.getItemName(item[item.length - 1]);
            return [last];
          }
          return [];
        }
        const grandNames = [];
        const names = [];
        item.forEach((list) => {
          if (Array.isArray(list)) {
            const name = list.map((it) => {
              return [...grandNames, this.getItemName(it)].join(this.separator);
            });
            names.push(name);
          } else {
            grandNames.push(this.getItemName(list));
          }
        });
        if (!this.multiple) {
          names.push(grandNames.join(this.separator));
        }
        return names;
      } else {
        // 省
        const name = this.getItemName(item);
        return [name];
      }
    },
    getCurrentTags(items) {
      if (this.multiple) {
        if (Array.isArray(items.multi) && items.multi.length) {
          return items.multi
            .map((item) => {
              return this.getItemTags(item, this.showPrefix);
            })
            .flat();
        }
      } else if (items.single) {
        return this.getItemTags(items.single, this.showPrefix);
      }
      return [];
    },
    getTags() {
      switch (this.type) {
        case this.optionValue.province:
          return this.getCurrentTags(this.province);
        case this.optionValue.city:
          return this.getCurrentTags(this.city);
        case this.optionValue.county:
          return this.getCurrentTags(this.county);
      }
    },
    getValues() {
      if (this.parentValue || this.isProvince) {
        return this.province;
      }
      if (this.isCity) {
        return this.city;
      }
      if (this.isCounty) {
        return this.county;
      }
    },
    getValue() {
      const values = this.getValues();
      if (this.multiple) {
        return values.multi;
      }
      return values.single;
    },
    emitChangeTitle(value) {
      this.$emit("update:text", value.join());
      this.$emit("change-text", value.join());
    },
    emitChangeValue(value) {
      this.$emit("update:value", value);
      this.$emit("change-value", value);
    },
    emitChange(obj) {
      this.$emit("change", obj);
    },
  },
};
</script>
